/**
 * TourButton
 * Button to manually trigger tours with smart availability based on current route
 */

import { Button } from '@shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu'
import { ROUTE_PATTERNS } from '@shared/config/routes'
import {
  useCanStartTour,
  useHomeTour,
  useProfessionalsListTour,
  useProfileTour,
  useProfessionalProfileFormTour,
  useTourManager,
} from '@shared/hooks'
import { HelpCircle } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

interface TourButtonProps {
  variant?: 'default' | 'ghost' | 'outline'
  size?: 'default' | 'sm' | 'lg' | 'icon'
  showLabel?: boolean
  'aria-label'?: string
}

export function TourButton({
  variant = 'ghost',
  size = 'sm',
  showLabel = false,
  'aria-label': ariaLabel,
}: TourButtonProps) {
  const location = useLocation()
  const { resetAllTours } = useTourManager()
  const canStartTour = useCanStartTour()
  const homeTour = useHomeTour()
  const professionalsListTour = useProfessionalsListTour()
  const profileTour = useProfileTour()
  const professionalProfileFormTour = useProfessionalProfileFormTour()

  // Track which profile form is visible
  const [isProfessionalFormVisible, setIsProfessionalFormVisible] = useState(false)
  const [isPersonalFormVisible, setIsPersonalFormVisible] = useState(false)

  // Watch for DOM changes to detect tab switching
  useEffect(() => {
    const checkVisibility = () => {
      const isProfessionalVisible =
        ROUTE_PATTERNS.PROFILE(location.pathname) &&
        document.querySelector('[data-tour="professional-profile"]') !== null
      const isPersonalVisible =
        ROUTE_PATTERNS.PROFILE(location.pathname) &&
        document.querySelector('[data-tour="personal-profile"]') !== null

      setIsProfessionalFormVisible(isProfessionalVisible)
      setIsPersonalFormVisible(isPersonalVisible)
    }

    // Initial check
    checkVisibility()

    // Set up observer for DOM changes (tab switching)
    const observer = new MutationObserver(checkVisibility)
    const targetNode = document.body
    observer.observe(targetNode, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [location.pathname])

  // Determine which tours are available for current route
  const availableTours = useMemo(() => {
    const path = location.pathname

    return [
      {
        id: 'home',
        name: 'Tour de Inicio',
        available: ROUTE_PATTERNS.HOME(path) || ROUTE_PATTERNS.PROFESSIONS(path),
        tour: homeTour,
      },
      {
        id: 'professionals-list',
        name: 'Tour de Profesionales',
        available: ROUTE_PATTERNS.PROFESSIONALS_LIST(path),
        tour: professionalsListTour,
      },
      {
        id: 'professional-profile-form',
        name: 'Tour de Formulario Profesional',
        available: isProfessionalFormVisible,
        tour: professionalProfileFormTour,
      },
      {
        id: 'profile',
        name: 'Tour de Perfil',
        available: isPersonalFormVisible,
        tour: profileTour,
      },
    ]
  }, [
    location.pathname,
    isProfessionalFormVisible,
    isPersonalFormVisible,
    homeTour,
    professionalsListTour,
    professionalProfileFormTour,
    profileTour,
  ])

  const handleResetAllTours = () => {
    if (
      confirm(
        '¿Estás seguro de que querés resetear todos los tours? Volverán a mostrarse automáticamente.'
      )
    ) {
      resetAllTours()
      alert(
        'Todos los tours han sido reseteados. Se mostrarán nuevamente cuando visites cada página.'
      )
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} aria-label={ariaLabel}>
          <HelpCircle className="h-4 w-4" />
          {showLabel && <span className="ml-2">Tours</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Tours Guiados</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {availableTours.map((tourItem) => (
          <DropdownMenuItem
            key={tourItem.id}
            onClick={() => tourItem.tour.startTour()}
            disabled={!tourItem.available || !canStartTour}
            className={!tourItem.available || !canStartTour ? 'text-muted-foreground' : ''}
          >
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>{tourItem.name}</span>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={handleResetAllTours} className="text-muted-foreground">
          Resetear Todos
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
